import type {
  SystemAnnouncement,
  SystemAnnouncementCategory,
  SystemAnnouncementLatestResponse,
  SystemAnnouncementListResponse,
  SystemAnnouncementResponse,
  SystemAnnouncementSubscription,
  SystemAnnouncementSubscriptionResponse,
} from '~/models/system-announcement';

const BASE = '/api/v1/admin/system-announcements';
const PUBLIC_BASE = '/api/v1/public/system-announcements';

export const systemAnnouncementService = {
  async latest(): Promise<ApiResult<SystemAnnouncementLatestResponse>> {
    return useMyFetch<SystemAnnouncementLatestResponse>(`${BASE}/latest`, { method: 'GET' });
  },

  async list({
    page,
    perPage,
    category,
  }: {
    page?: number;
    perPage?: number;
    category?: SystemAnnouncementCategory | null;
  } = {}): Promise<ApiResult<SystemAnnouncementListResponse>> {
    return useMyFetch<SystemAnnouncementListResponse>(BASE, {
      method: 'GET',
      params: { page, per_page: perPage, category: category || undefined },
    });
  },

  async show(uid: string): Promise<ApiResult<SystemAnnouncementResponse>> {
    return useMyFetch<SystemAnnouncementResponse>(`${BASE}/${uid}`, { method: 'GET' });
  },

  async create(attributes: Partial<SystemAnnouncement>): Promise<ApiResult<SystemAnnouncementResponse>> {
    return useMyFetch<SystemAnnouncementResponse>(BASE, {
      method: 'POST',
      body: attributes,
    });
  },

  async update(uid: string, updated: Partial<SystemAnnouncement>): Promise<ApiResult<SystemAnnouncementResponse>> {
    return useMyFetch<SystemAnnouncementResponse>(`${BASE}/${uid}`, {
      method: 'PUT',
      body: updated,
    });
  },

  async remove(uid: string): Promise<ApiResult<EmptyResponse>> {
    return useMyFetch<EmptyResponse>(`${BASE}/${uid}`, { method: 'DELETE' });
  },

  async showPublic(uid: string): Promise<ApiResult<SystemAnnouncementResponse>> {
    return useMyFetch<SystemAnnouncementResponse>(`${PUBLIC_BASE}/${uid}`, {
      method: 'GET',
      // Public endpoint: no auth header, no auto-redirect to signin on 401.
      headers: { Authorization: '' },
    });
  },

  async fetchSubscription(organizationId: number): Promise<ApiResult<SystemAnnouncementSubscriptionResponse>> {
    return useMyFetch<SystemAnnouncementSubscriptionResponse>(
      `/api/v1/admin/organizations/${organizationId}/system-announcements/subscription`,
      { method: 'GET' },
    );
  },

  async updateSubscription(
    organizationId: number,
    payload: SystemAnnouncementSubscription,
  ): Promise<ApiResult<SystemAnnouncementSubscriptionResponse>> {
    return useMyFetch<SystemAnnouncementSubscriptionResponse>(
      `/api/v1/admin/organizations/${organizationId}/system-announcements/subscription`,
      { method: 'PUT', body: payload },
    );
  },
};
